import { useEffect, useMemo, useState } from "react";
import { FaSort, FaSortUp, FaSortDown } from "react-icons/fa";
import { useNavigate } from "react-router";
import { toast } from "react-toastify";
import { DEFINED_PERMISSIONS } from "../../../../../utils/const";
import { FormatDateTime } from "../../../../../utils/functions/ResourceFunctions";
import { useSearchFilter } from "../../../../../utils/functions/useDate";
import {
  useApproveDeclineObjectiveMutation,
  useDeleteAppraisalObjectiveMutation,
  useGetAppraisalObjectiveMainQuery,
  useGetHrDepartmentsQuery,
  useGetJobRoleQuery,
} from "../../../../api/hrApi";
import DashboardStyle from "../../../../dashboard/Styles/Dashboard.module.css";
import { CTAButtons } from "../../../../global/components/Buttons/buttons";
import PageStyle from "../../../Components/Layout/PageLayout";
import { TableActions } from "../../../Components/Misc/Actions";
import DeleteItemModal from "../../../Components/Modals/DeleteItemModal";
import { AppModalTemplate } from "../../../Components/Modals/Modals";
import Pagination from "../../../Components/Pagination/Pagination";
import {
  CalendarFilter2,
  ProDropFilter,
  SearchFilter,
} from "../../../Components/Search/Search";
import Table from "../../../Components/Table/Table";
import ApproveModal from "./Modals/ApproveModal";
import { EditObjectivesModal } from "./Modals/EditObjectiveModal";
import { ObjectivesModal1 } from "./Modals/ObjectivesModal1";
import { FilterByStatus, FilterObjectiveByStatus } from "./status";

export const CreateObjectives = () => {
  const {
    searchText,
    filter,
    setSearchText,
    setFilter,
    pageSize,
    setPageSize,
    setCurrentPage,
    currentPage,
  } = useSearchFilter({
    initialFilter: 1,
  });

  const [openModal, setOpenModal] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [onDelete] = useDeleteAppraisalObjectiveMutation();
  const [selectedId, setSelectedId] = useState(null);
  const [openDelete, setOpenDelete] = useState(false);
  const [matchedPermissionHr, setMatchedPermissionHr] = useState("");
  const [yearFilter, setYearFilter] = useState("All Years");
  const [sortColumn, setSortColumn] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  const [departmentId, setDepartmentId] = useState('');
  const [jobRoleId, setJobRoleId] = useState('');
  const { data: JobRole } = useGetJobRoleQuery({
    ApprovalStatus: 0,
    PageSize: 9999,
    PageNumber: 1
  }); 
  const { data, isLoading } = useGetAppraisalObjectiveMainQuery({
    PageNumber: currentPage, DepartmentId: departmentId,
    PageSize: pageSize, JobRoleId: jobRoleId,
    GeneralSearch: searchText,
    ApprovalStatus: filter,
    StartDate: startDate,
    EndDate: endDate,
    year: yearFilter === "All Years" ? "" : yearFilter || "",
  });
  useEffect(() => {
    const userPerm = JSON.parse(localStorage.getItem("x-u-perm"));
    const hrPerm = [
      DEFINED_PERMISSIONS.SeniorHRManager,
      DEFINED_PERMISSIONS.PrimaryHRManager,
    ];

    if (userPerm && userPerm.some((perm) => hrPerm.includes(perm))) {
      const matchedPerm = userPerm.find((perm) => hrPerm.includes(perm));
      setMatchedPermissionHr(matchedPerm);
    } else {
      setMatchedPermissionHr("");
    }
  }, []);
  const navigate = useNavigate();
  const handleModal = () => {
    setOpenModal(!openModal);
  };
  const [approveDecline, { isLoading: isApproving }] =
    useApproveDeclineObjectiveMutation();
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const declineAction = async (type) => {
    try {
      const response = await approveDecline({
        objectiveIds: selectedItems,
        approvalType: type,
      }).unwrap();
      if (response?.successful) {
        setOpenApproveModal(false);
        setSelectedItems([]); // Clear selections after successful approval
        setSelectAll(false);
        toast.success(
          response?.statusMessage || "Objectives approved successfully"
        );
      }
    } catch (error) {
      toast.error(
        error?.data?.statusMessage ?? "Error approving Appraisal Objectives"
      );
      console.log(error);
    }
  };
  const handleApproveModal = () => setOpenApproveModal(!openApproveModal);

  // Selection handling functions
  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      const allIds = data?.result?.map((item) => item.id) || [];
      setSelectedItems(allIds);
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelection = (item, checked) => {
    if (checked) {
      setSelectedItems((prev) => [...prev, item.id]);
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== item.id));
      setSelectAll(false);
    }
  };

  const isItemSelected = (itemId) => {
    return selectedItems.includes(itemId);
  };

  // Update selectAll state when individual items are selected
  useEffect(() => {
    if (
      data?.result?.length > 0 &&
      selectedItems.length === data.result.length
    ) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedItems, data?.result]);

  // Handle batch approval
  const handleBatchApproval = () => {
    if (selectedItems.length === 0) {
      toast.error("Please select at least one objective to approve");
      return;
    }
    setOpenApproveModal(true);
  };
  const actionButton = (
    <>
      <CTAButtons onClick={handleModal}>Create Objectives</CTAButtons>
    </>
  );
  const onValidate = () => {
    setOpenModal(false);
  };
  const handleOpenDeleteModal = (id) => {
    if (id) {
      setSelectedId(id);
      setOpenDelete(true);
    }
  };
  const handleCloseDelete = () => {
    setSelectedId(null);
    setOpenDelete(false);
  };
  const handleDelete = async () => {
    try {
      const response = await onDelete({
        id: parseInt(selectedId),
      }).unwrap();
      if (response?.successful) {
        handleCloseDelete();
        toast.success(response?.statusMessage);
      } else {
        toast.error(response?.data?.title);
      }
    } catch (error) {
      toast.error(error.message ?? "Error deleting Counselling Type");
    }
  };
  const handleView = (id) => {
    if (id) {
      navigate(`${id}/view`);
    }
  };
  const handleEdit = (id) => {
    if (id) {
      setSelectedId(id);
      handleModal();
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const sortedData = useMemo(() => {
    if (!data?.result) return [];
    return [...data.result].sort((a, b) => {
      if (!sortColumn) return 0;
      let aValue;
      let bValue;

      if (sortColumn === "designation.designationName") {
        aValue = a.designation?.designationName;
        bValue = b.designation?.designationName;
      } else {
        aValue = a[sortColumn];
        bValue = b[sortColumn];
      }

      if (sortColumn === "weight") {
        aValue = parseFloat(aValue);
        bValue = parseFloat(bValue);
      }

      if (sortColumn === "year") {
        aValue = parseInt(aValue);
        bValue = parseInt(bValue);
      }

      if (sortColumn === "createdDate") {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // For strings, make case-insensitive
      if (typeof aValue === "string") {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [data?.result, sortColumn, sortDirection]);

  const renderSortIcon = (column) => {
    if (sortColumn !== column) return <FaSort className="ml-1" />;
    return sortDirection === "asc" ? (
      <FaSortUp className="ml-1" />
    ) : (
      <FaSortDown className="ml-1" />
    );
  };

  const currentYear = new Date().getFullYear() + 1;
  const dates = [
    // { name: "All Years", filter: "" },
    ...Array.from({ length: currentYear - 1999 + 1 }, (_, i) => ({
      name: (currentYear - i).toString(),
      filter: (currentYear - i).toString(),
    })),
  ];
  const { data: departments } = useGetHrDepartmentsQuery();

  return (
    <PageStyle
      action={actionButton}
      hasBack={true}
      backURL={"/dashboard/hr/HR-Appraisal-Setup"}
      support={
        "Create appraisal objectives for each appraisee under supervision"
      }
      demoTitle={"Manage Appraisal Objectives"}
      hasTopPageDemo
    >
      <div>
        <div>
          <form className={DashboardStyle.dashboard_filter}>
            <SearchFilter
              value={searchText}
              text={(searchText) => setSearchText(searchText)}
            />
            <ProDropFilter
              filter={filter}
              setFilter={setFilter}
              name={"Status"}
              filterBy={FilterByStatus}
            />

            <CalendarFilter2
              name="Select Date Range"
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              normalDate
            />
            <ProDropFilter
              filter={yearFilter}
              setFilter={setYearFilter}
              name={"Year"}
              filterBy={dates}
            />
            <ProDropFilter
              filter={departmentId}
              setFilter={setDepartmentId}
              name={"Deparment"}
              filterBy={departments?.responseObject?.pageItems?.map(depatment => ({
                name: depatment.name, filter: depatment.id
              }))}
            />
            <ProDropFilter
              filter={jobRoleId}
              setFilter={setJobRoleId}
              name={"Job Role"}
              filterBy={JobRole?.responseObject?.pageItems?.map(job => ({
                name: job.designationName, filter: job.id
              }))}
            />
          </form>
        </div>
        <div>
          <CTAButtons
            onClick={handleBatchApproval}
            disabled={selectedItems.length === 0}
          >
            Approve Selected Objectives ({selectedItems.length})
          </CTAButtons>
        </div>
      </div>
      <div className={DashboardStyle.dashboard_table_holder}>
        <Table>
          <thead>
            <tr>
              <th>S/N</th>
              <th>
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={(e) => handleSelectAll(e.target.checked)}
                  className="sr-only"
                />
                <div
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer ${selectAll
                      ? "bg-[#18BB0C] border-[#18BB0C]"
                      : "bg-white border-gray-300 hover:border-gray-400"
                    }`}
                  onClick={(e) => {
                    e.preventDefault();
                    handleSelectAll(!selectAll);
                  }}
                >
                  {selectAll && (
                    <svg
                      className="w-3 h-3 text-white"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </th>
              <th
                onClick={() => handleSort("title")}
                style={{ cursor: "pointer" }}
              >
                Title {renderSortIcon("title")}
              </th>
              <th
                onClick={() => handleSort("designation.designationName")}
                style={{ cursor: "pointer" }}
              >
                Job Role {renderSortIcon("designation.designationName")}
              </th>
              <th
                onClick={() => handleSort("departmentName")}
                style={{ cursor: "pointer" }}
              >
                Department {renderSortIcon("departmentName")}
              </th>
              <th
                onClick={() => handleSort("year")}
                style={{ cursor: "pointer" }}
              >
                Year {renderSortIcon("year")}
              </th>
              <th
                onClick={() => handleSort("weight")}
                style={{ cursor: "pointer" }}
              >
                Weight (%) {renderSortIcon("weight")}
              </th>
              <th
                onClick={() => handleSort("createdDate")}
                style={{ cursor: "pointer" }}
              >
                Created Date {renderSortIcon("createdDate")}
              </th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="text-center">
                  Loading...
                </td>
              </tr>
            ) : sortedData?.length > 0 ? (
              sortedData?.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>
                    <input
                      type="checkbox"
                      checked={isItemSelected(item.id)}
                      onChange={(e) => handleSelection(item, e.target.checked)}
                      className="sr-only"
                    />
                    <div
                      className={`w-4 h-4 rounded border-2 flex items-center justify-center cursor-pointer ${isItemSelected(item.id)
                          ? "bg-[#18BB0C] border-[#18BB0C]"
                          : "bg-white border-gray-300 hover:border-gray-400"
                        }`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleSelection(item, !isItemSelected(item.id));
                      }}
                    >
                      {isItemSelected(item.id) && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </td>
                  <td>{item?.title}</td>
                  <td>{item?.designation?.designationName}</td>
                  <td>{item?.departmentName}</td>
                  <td>{item?.year}</td>
                  <td>{item?.weight}%</td>
                  <td>{FormatDateTime(item?.createdDate, "DD/MM/YYYY")}</td>
                  <td>{item?.approvalStatus}</td>

                  <td>
                    <TableActions
                      hasChildren={true}
                      url={matchedPermissionHr ? `${item.id}/view` : null}
                    >
                      {[

                        {
                          name: matchedPermissionHr ? "View" : null,
                          action: () =>
                            matchedPermissionHr ? handleView(item.id) : null,
                        },
                        {
                          name: item?.approvalStatus === "Approved" ? null : "Edit",
                          action: () =>
                            item?.approvalStatus === "Approved"
                              ? null
                              : handleEdit(item.id),
                        },
                        {
                          name: item?.approvalStatus === "Approved" ? null : "Delete",
                          action: () =>
                            item?.approvalStatus === "Approved"
                              ? null
                              : handleOpenDeleteModal(item.id),
                        },
                      ]}
                    </TableActions>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={10} className="text-center">
                  No Objectives Found
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
      <Pagination
        last_page={data?.responseObject?.previousPage}
        present_page={data?.currentPage}
        totalRows={data?.totalPages}
        pageSize={data?.pageSize}
        setPageSize={(page) => setPageSize(page)}
        click={(page) => setCurrentPage(page)}
      />
      {openModal && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openModal}
          setIsOpen={handleModal}
        >
          <ObjectivesModal1
            onValidate={onValidate}
            isOpen={openModal}
            setIsOpen={setOpenModal}
          />
        </AppModalTemplate>
      )}
      {openModal && selectedId && (
        <AppModalTemplate
          width={"600px"}
          isOpen={openModal}
          setIsOpen={handleModal}
        >
          <EditObjectivesModal
            isOpen={openModal}
            setIsOpen={setOpenModal}
            onValidate={handleModal}
            id={selectedId}
          />
        </AppModalTemplate>
      )}
      <DeleteItemModal
        isOpen={openDelete}
        handleClose={handleCloseDelete}
        handleDelete={handleDelete}
        headText={"Delete Score Based Recommendation"}
        infoText={
          "Are you sure you want to Delete this Score Based Recommendation?"
        }
      />
      <ApproveModal
        isOpen={openApproveModal}
        handleClose={handleApproveModal}
        handleDelete={() => declineAction(0)}
        isLoading={isApproving}
        headText={`Approve Selected Objectives (${selectedItems.length})`}
      />
    </PageStyle>
  );
};
